import { SparkleIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useMemo } from "react";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileHeading from "common/components/ProfileHeading";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { typedObjectEntries } from "common/helpers/utils";

import { LIST_LEADS_ACTIONS, LIST_LEADS_COLUMNS } from "../../../../constants";
import LeadListLeadSources from "../LeadListLeadSources";
import BlacklistLeads from "../ManageLeads/BlacklistLeads";
import RemoveLeads from "../ManageLeads/RemoveLeads";
import UnblacklistLeads from "../ManageLeads/UnblacklistLeads";
import EditVariableValue from "./EditVariableValue";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { ListLead } from "../../../../types";

interface DesktopTableProps {
  listId: string;
  leads: ListLead[];
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  selectedLeads: ListLead[];
  setSelectedLeads: (selectedLeads: ListLead[]) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
  //every variable key stored for the list, one extra column is added per key
  customVariableKeys: string[];
}

export default function DesktopTable({
  listId,
  leads,
  isLoading,
  pageSize,
  totalItemCount,
  selectedLeads,
  setSelectedLeads,
  columnVisibility,
  setColumnVisibility,
  customVariableKeys,
}: DesktopTableProps) {
  const columns = useMemo<ColumnDef<ListLead>[]>(() => {
    const columns: ColumnDef<ListLead>[] = [
      {
        header: "Name",
        cell: ({ row: { original } }) => {
          const {
            picture_url,
            full_name,
            blacklisted,
            company_blacklisted,
            occupation,
            source_ids,
          } = original;

          return (
            <ProfileHeading
              fullName={full_name}
              occupation={occupation}
              pictureUrl={picture_url}
              isLoading={isLoading}
              extraComponent={
                blacklisted || company_blacklisted ? (
                  <Tag variant="danger" className="shrink-0">
                    Blacklisted
                  </Tag>
                ) : (
                  <LeadListLeadSources
                    listId={listId}
                    sourceIds={source_ids}
                    isLoading={isLoading}
                  />
                )
              }
            />
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(LIST_LEADS_COLUMNS).map(
        ([_, { label, icon, renderTag }]): ColumnDef<ListLead> => ({
          header: label,
          cell: ({ row: { original } }) => {
            if (isLoading) {
              return <Skeleton className="h-4 w-32" />;
            }
            return renderTag(original);
          },
          meta: { icon, className: "min-w-40 max-w-40" },
        }),
      ),
    ];

    if (customVariableKeys) {
      columns.push(
        ...customVariableKeys.map(
          (key): ColumnDef<ListLead> => ({
            //prefixed so a variable key can't collide with a static column's id
            id: `variable-${key}`,
            header: key.replaceAll("_", " ").toLowerCase(),
            cell: ({ row: { original } }) => {
              if (isLoading) {
                return <Skeleton className="h-4 w-32 mx-2.5" />;
              }
              return (
                <EditVariableValue
                  initialValue={original.custom_variables[key]}
                  listId={listId}
                  targetUrn={original.urn}
                  variableKey={key}
                />
              );
            },
            meta: {
              icon: SparkleIcon,
              className: "min-w-40 max-w-40",
              headClassName: "[&_svg]:text-magenta-500",
              bodyClassName: clsx("bg-magenta-50/30", !isLoading && "!p-0"),
            },
          }),
        ),
      );
    }

    return columns;
  }, [isLoading, customVariableKeys]);

  const table = useDataTable(
    {
      data: leads || new Array(pageSize).fill({}),
      columns,
    },
    {
      selectedRows: selectedLeads,
      onRowSelectionChange: setSelectedLeads,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
  );

  return (
    <DataTable
      table={table}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
      selectionActionsComponent={
        <BulkManageDesktop
          actions={LIST_LEADS_ACTIONS}
          selection={selectedLeads}
          clearSelection={() => setSelectedLeads([])}
          renderActionComponent={({ action, onClose }) => {
            const props = {
              listId,
              selectedLeads,
              onClose: (shouldClearSelection: boolean) => {
                onClose();
                if (shouldClearSelection) {
                  setSelectedLeads([]);
                }
              },
            };
            if (action === "blacklist") {
              return <BlacklistLeads {...props} />;
            }
            if (action === "unblacklist") {
              return (
                <UnblacklistLeads
                  selectedLeads={selectedLeads}
                  onClose={props.onClose}
                />
              );
            }
            return <RemoveLeads {...props} />;
          }}
        />
      }
    />
  );
}
